import { useEffect, useRef } from "react";
import type { StoryListItem, RealmId } from "@chuzi/shared/types";

interface StoryPickerProps {
  stories: StoryListItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  realm: RealmId;
}

export function StoryPicker({
  stories,
  selectedIndex,
  onSelect,
  realm,
}: StoryPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const isWilds = realm === "wilds";
  const accentSolid = isWilds ? "#78c878" : "#7eb8ff";
  const accentGlow = isWilds ? "rgba(120,200,120,0.3)" : "rgba(126,184,255,0.3)";

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex > 0) onSelect(selectedIndex - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex < stories.length - 1) onSelect(selectedIndex + 1);
    }
  }

  if (stories.length <= 1) return null;

  const canLeft = selectedIndex > 0;
  const canRight = selectedIndex < stories.length - 1;

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        position: "absolute",
        bottom: 44,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 25,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        outline: "none",
        animation: "hudFadeIn 0.4s ease-out",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
        }}
      >
        <button
          type="button"
          aria-label="Previous story"
          disabled={!canLeft}
          onClick={() => canLeft && onSelect(selectedIndex - 1)}
          style={{
            background: "none",
            border: "none",
            cursor: canLeft ? "pointer" : "default",
            padding: 8,
            opacity: canLeft ? 0.7 : 0.15,
            transition: "opacity 300ms",
            pointerEvents: "auto",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 3L5 8l5 5"
              stroke={accentSolid}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 48,
            justifyContent: "center",
          }}
        >
          {stories.map((story, i) => {
            const active = i === selectedIndex;
            return (
              <button
                key={story.id}
                type="button"
                aria-label={`Story ${i + 1} of ${stories.length}`}
                aria-current={active ? "true" : undefined}
                onClick={() => onSelect(i)}
                style={{
                  width: active ? 8 : 6,
                  height: active ? 8 : 6,
                  borderRadius: "50%",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background: active ? accentSolid : accentGlow,
                  boxShadow: active ? `0 0 10px ${accentGlow}` : "none",
                  transition: "width 200ms, height 200ms, background 200ms, box-shadow 200ms",
                  pointerEvents: "auto",
                }}
              />
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Next story"
          disabled={!canRight}
          onClick={() => canRight && onSelect(selectedIndex + 1)}
          style={{
            background: "none",
            border: "none",
            cursor: canRight ? "pointer" : "default",
            padding: 8,
            opacity: canRight ? 0.7 : 0.15,
            transition: "opacity 300ms",
            pointerEvents: "auto",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 3l5 5-5 5"
              stroke={accentSolid}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
